import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useSession } from '../auth/session'
import { ApiError } from '../lib/api'
import { Field, inputClass } from './SignIn'

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    // Mirrors the server's DPL__Auth__MinimumPasswordLength. The server is the
    // control; this is so the user finds out before a round trip.
    newPassword: z.string().min(10, 'Use at least 10 characters'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'Choose a password you have not used here before',
    path: ['newPassword'],
  })

type Values = z.infer<typeof schema>

/**
 * Shown when the server says a password change is due (§11, forced reset on first
 * login). The server refuses every other endpoint until it is done, so this
 * screen is the user-facing half of a rule that is already enforced.
 */
export function ChangePassword() {
  const changePassword = useSession((s) => s.changePassword)
  const user = useSession((s) => s.user)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) })

  const onSubmit = async (values: Values) => {
    setError(null)
    try {
      await changePassword(values.currentPassword, values.newPassword)
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.title : 'Could not change the password.')
    }
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dpl-bg px-4">
        <div className="w-full max-w-sm rounded-lg border border-dpl-border bg-dpl-surface p-6 text-center shadow-sm">
          <p className="text-sm font-medium text-dpl-success">Password changed</p>
          <p className="mt-2 text-sm text-dpl-muted">
            Every session was signed out, including this one. Sign in with your new password.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-dpl-bg px-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm rounded-lg border border-dpl-border bg-dpl-surface p-6 shadow-sm"
        noValidate
      >
        <h1 className="text-base font-semibold text-dpl-text">Choose a new password</h1>
        <p className="mb-5 mt-1 text-sm text-dpl-muted">
          {user ? `${user.name}, your` : 'Your'} account still uses the password it was created
          with. Change it before continuing.
        </p>

        {error && (
          <p
            role="alert"
            className="mb-4 rounded border border-dpl-danger/30 bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger"
          >
            {error}
          </p>
        )}

        <Field label="Current password" error={errors.currentPassword?.message}>
          <input
            {...register('currentPassword')}
            type="password"
            autoComplete="current-password"
            className={inputClass(!!errors.currentPassword)}
          />
        </Field>

        <Field label="New password" error={errors.newPassword?.message}>
          <input
            {...register('newPassword')}
            type="password"
            autoComplete="new-password"
            className={inputClass(!!errors.newPassword)}
          />
        </Field>

        <Field label="Confirm new password" error={errors.confirmPassword?.message}>
          <input
            {...register('confirmPassword')}
            type="password"
            autoComplete="new-password"
            className={inputClass(!!errors.confirmPassword)}
          />
        </Field>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-2 w-full rounded-md bg-dpl-primary py-2.5 text-sm font-medium text-white transition hover:bg-dpl-primary-hover disabled:opacity-60"
        >
          {isSubmitting ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </div>
  )
}
